import { useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import { SocialIcon } from "react-social-icons";

import { Composer, Container, MessageList, useWebchat } from "@botpress/webchat";
import { AddComment, PermIdentity } from "@mui/icons-material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MenuIcon from "@mui/icons-material/Menu";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import VoiceOverOffIcon from "@mui/icons-material/VoiceOverOff";
import { Stack } from "@mui/material";
import { alpha, Box, Typography } from "@mui/material";
import { Button } from "@mui/material";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";

import constants from "../../assets/constants.js";
import { convertTextToSpeech } from "../../utils/googleTts.js";
import AppBarIconButton from "../ui/appBarIconButton";
import OmniAvatar from "../ui/omniAvatar.js";
import OmniMenu from "../ui/omniMenu";
import OmniTextToSpeechPlayer from "../ui/OmniTextToSpeechPlayer.js";
import Portal from "../ui/portal";
import PortalAppBar from "../ui/portalAppBar.js";
import PortalGridContent from "../ui/portalGridContent.js";

const DemoCUPortal = props => {
  const theme = useTheme();

  let webchatClientID = "ecf4713a-93e5-4b3e-a90f-8e68c854fdd7";

  //Flag Defs:
  //  languageCode - the code
  //  tts- bool

  //#region languageCode and TTS Parameter Handling
  const checkForLanguageCodeParam = () => {
    const urlParams = new URLSearchParams(window.location.search);
    for (var [key, value] of urlParams.entries()) {
      if (key === "languageCode") {
        return value;
      }
    }
    return "en-US";
  };

  const [languageCode] = useState(checkForLanguageCodeParam());

  const [ttsValue, setTTSValue] = useState(false);

  useEffect(() => {
    if (ttsValue != null) {
      setIsWebchatOpen(true);
    }
  }, [ttsValue]);

  //#endregion

  const config = {
    botDescription: "SchoolsFirst FCU Member Concierge",
    botName: "Benjamin",
    botAvatar:
      "https://omni-public-media.s3.us-west-2.amazonaws.com/branding/SchoolsFirstFCU/botAvatar.png",
    title: "Schools First Credit Union",
    customColors: {
      commonGround: theme.palette.commonground.main,
      primaryContrastText: theme.palette.primary.contrastText,
      secondaryContrastText: theme.palette.secondary.contrastText,
      primaryColor: theme.palette.primary.main,
      secondaryColor: theme.palette.secondary.main,
    },
    // socialMedia: [
    //   "https://www.linkedin.com/company/schoolsfirst-federal-credit-union",
    //   "https://x.com/SchoolsFirstFCU",
    //   "https://www.facebook.com/SchoolsFirstFCU",
    // ],
  };

  const [isWebchatOpen, setIsWebchatOpen] = useState(false);

  const { client, messages, isTyping, user, clientState, newConversation, on } = useWebchat({
    clientId: webchatClientID, // Insert your Client ID here

    storageKey: `botpress-DemoCUPortal-${webchatClientID}`,
    storageLocation: "localStorage",
  });

  const enrichedMessages = useMemo(
    () =>
      messages.map(message => {
        const { authorId } = message;
        const direction = authorId === user?.userId ? "outgoing" : "incoming";
        return {
          ...message,
          direction,
          sender:
            direction === "outgoing"
              ? { name: user?.name ?? "You", avatar: user?.pictureUrl }
              : { name: config.botName ?? "Bot", avatar: config.botAvatar },
        };
      }),
    [config.botAvatar, config.botName, messages, user?.userId, user?.name, user?.pictureUrl],
  );

  //#region User Data Update
  const [userInitialized, SetUserInitialized] = useState(false);

  useEffect(() => {
    if (client && !userInitialized) {
      try {
        client.updateUser({
          data: {
            demoId: "0",
            languageCode: languageCode,
            origin: constants.botpressConversationOriginEnums.Portal,
          },
        });
        SetUserInitialized(true);
      } catch (error) {
        console.error(error);
      }
    }
  }, [client, userInitialized, languageCode]);
  //#endregion

  //#region Menu HandlersOmni Header
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  //#endregion

  //#region Text to Speech Player Handlers

  const [volume, setVolume] = useState(0.5);
  const [textToSpeechQueue, setTextToSpeechQueue] = useState([]);

  const [speechConfig] = useState({
    voiceName: "Despina",
    languageCode: languageCode,
    speakingRate: 1.2,
  });

  useEffect(() => {
    const unsubscribe = on("message", message => {
      if (message.authorId !== user?.userId) {
        try {
          let messageText = message.block.block.text;
          convertTextToSpeech(messageText, speechConfig).then(url => {
            setTextToSpeechQueue(prev => [...prev, url]);
          });
        } catch (error) {
          console.error("Error in TTS message handling:", error);
        }
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [on, speechConfig, user]);

  //#endregion
  const startWebchat = userId => {
    newConversation().then(() => {
      client.updateUser({
        data: {
          demoId: userId.toString(),
          languageCode: languageCode,
          origin: constants.botpressConversationOriginEnums.Portal,
        },
      });
      setIsWebchatOpen(true);
    });
  };

  return (
    <Portal
      displayHeader={false}
      sx={{
        backgroundSize: "cover",
        border: "none",
        backgroundColor: theme.palette.commonground.main,
      }}
    >
      <PortalAppBar
        title="Sales Behind PIN Demo"
        belowTitle="Schools First Credit Union"
        hideAvatar={true}
      />

      <PortalGridContent
        hasPortalAppBar={true}
        size={{ xs: 12, md: 6 }}
        width={"100%"}
        height={"100%"}
      >
        <Grid container item spacing={2} size={{ xs: 12 }}>
          <Grid item spacing={2} size={{ xs: 12, sm: 8 }}>
            {/* Marketing Site */}
            <Grid
              item
              container
              padding={0}
              size={{ xs: 12 }}
              sx={{
                borderRadius: theme.spacing(1),
                padding: "4px",
                display: "flex",
                flexDirection: "row",
                backgroundColor: theme.palette.primary.contrastText,
                border: `1px solid ${alpha(theme.palette.common.black, 0.15)}`,
                boxShadow: theme.shadows[3],
                marginBottom: theme.spacing(2),
                overflow: "hidden",
              }}
            >
              <Grid container item size={{ xs: 12, sm: 9 }} flexDirection={"row"}>
                {/* Left Icon Section */}
                <Grid container item size={{ xs: 12, sm: 3 }} sx={{ padding: "4px" }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: "140px",
                      // Grey background
                      backgroundColor: theme.palette.grey[200],
                      borderRadius: theme.spacing(0.5),
                      // Centering the icon
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <PermIdentity
                      sx={{
                        // Scaling logic:
                        // It will be 40% of the container width, but capped between 40px and 100px
                        fontSize: "clamp(40px, 8vw, 100px)",
                        color: theme.palette.grey[500],
                      }}
                    />
                  </Box>
                </Grid>

                {/* Center Text Content */}
                <Grid container item size={{ xs: 12, sm: 9 }}>
                  <Stack sx={{ paddingRight: theme.spacing(2), paddingBottom: theme.spacing(1) }}>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                      <Typography
                        variant="h5"
                        sx={{
                          marginTop: theme.spacing(0.5),
                          marginLeft: theme.spacing(1),
                        }}
                      >
                        {"Anonymous User"}
                      </Typography>
                    </Box>
                    <Divider
                      sx={{
                        width: "calc(100% - 8px)",
                        marginLeft: theme.spacing(1),
                        marginTop: "0px",
                        marginBottom: "8px",
                        background: `linear-gradient(to right, ${theme.palette.text.primary}, transparent)`,
                        border: "none",
                        height: "2px",
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        paddingLeft: theme.spacing(1),
                        lineHeight: 1.5,
                      }}
                    >
                      This visitor found the credit union through an online search while looking for
                      ways to reduce monthly expenses. They are not yet a member and are hesitant to
                      speak directly with a financial institution because they worry they will
                      immediately be sold to. They noticed the chatbot while browsing the marketing
                      site and decided to ask a few low-pressure questions first before deciding
                      whether the credit union feels trustworthy and helpful.
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              {/* Right Action Block */}
              <Grid
                item
                size={{ xs: 12, sm: 3 }}
                sx={{
                  display: "flex",
                  padding: theme.spacing(1),
                }}
              >
                <Button
                  sx={{
                    height: "auto",
                    borderRadius: theme.spacing(1),
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  tooltipTitle="Start Conversation"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    startWebchat(0);
                  }}
                >
                  <Stack alignItems="center">
                    <Typography
                      variant="h5"
                      sx={{
                        borderBottom: `2px solid`,
                        margin: theme.spacing(1),
                        paddingBottom: theme.spacing(1),
                        marginBottom: 0,
                      }}
                    >
                      {"Anonymous User"}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        margin: theme.spacing(1),
                      }}
                    >
                      Click Here
                    </Typography>
                  </Stack>
                </Button>
              </Grid>
            </Grid>
            {/* User 1 */}
            <Grid
              item
              container
              padding={0}
              size={{ xs: 12 }}
              sx={{
                borderRadius: theme.spacing(1),
                padding: "4px",
                display: "flex",
                flexDirection: "row",
                backgroundColor: theme.palette.primary.contrastText,
                border: `1px solid ${alpha(theme.palette.common.black, 0.15)}`,
                boxShadow: theme.shadows[3],
                marginBottom: theme.spacing(2),
                overflow: "hidden",
              }}
            >
              <Grid container item size={{ xs: 12, sm: 9 }}>
                {/* Left Image Section */}
                <Grid container item size={{ xs: 12, sm: 3 }} sx={{ padding: "4px" }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: "140px",
                      backgroundImage: `url("https://omni-public-media.s3.us-west-2.amazonaws.com/test/demo/userImages/userImage(1).jpg")`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      borderRadius: theme.spacing(0.5),
                    }}
                  />
                </Grid>

                {/* Center Text Content */}
                <Grid container item size={{ xs: 12, sm: 9 }}>
                  <Stack sx={{ paddingRight: theme.spacing(2), paddingBottom: theme.spacing(1) }}>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                      <Typography
                        variant="h5"
                        sx={{
                          marginTop: theme.spacing(0.5),
                          marginLeft: theme.spacing(1),
                        }}
                      >
                        {"Becky Johnson"}
                      </Typography>
                    </Box>
                    <Divider
                      sx={{
                        width: "calc(100% - 8px)",
                        marginLeft: theme.spacing(1),
                        marginTop: "0px",
                        marginBottom: "8px",
                        background: `linear-gradient(to right, ${theme.palette.text.primary}, transparent)`,
                        border: "none",
                        height: "2px",
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        paddingLeft: theme.spacing(1),
                        lineHeight: 1.5,
                      }}
                    >
                      Becky has spent years building financial stability for her family and recently
                      renovated parts of her home. Between rising expenses, several credit cards,
                      and a personal loan from another bank, her monthly payments have started
                      feeling harder to manage. She has strong credit and substantial equity in her
                      home, but she wants a smarter way to consolidate debt and reduce financial
                      stress without disrupting her lifestyle.
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              {/* Right Action Block */}
              <Grid
                item
                size={{ xs: 12, sm: 3 }}
                sx={{
                  display: "flex",
                  padding: theme.spacing(1),
                }}
              >
                <Button
                  sx={{
                    height: "auto",
                    borderRadius: theme.spacing(1),
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  tooltipTitle="Start Conversation"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    startWebchat(1);
                  }}
                >
                  <Stack alignItems="center">
                    <Typography
                      variant="h5"
                      sx={{
                        borderBottom: `2px solid`,
                        margin: theme.spacing(1),
                        paddingBottom: theme.spacing(1),
                        marginBottom: 0,
                      }}
                    >
                      {"Becky Johnson"}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        margin: theme.spacing(1),
                      }}
                    >
                      Click Here
                    </Typography>
                  </Stack>
                </Button>
              </Grid>
            </Grid>
            {/* User 2 */}
            <Grid
              item
              container
              padding={0}
              size={{ xs: 12 }}
              sx={{
                borderRadius: theme.spacing(1),
                padding: "4px",
                display: "flex",
                flexDirection: "row",
                backgroundColor: theme.palette.primary.contrastText,
                border: `1px solid ${alpha(theme.palette.common.black, 0.15)}`,
                boxShadow: theme.shadows[3],
                marginBottom: theme.spacing(2),
                overflow: "hidden",
              }}
            >
              <Grid container item size={{ xs: 12, sm: 9 }} flexDirection={"row"}>
                {/* Left Image Section */}
                <Grid container item size={{ xs: 12, sm: 3 }} sx={{ padding: "4px" }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: "140px",
                      backgroundImage: `url("https://omni-public-media.s3.us-west-2.amazonaws.com/test/demo/userImages/userImage(2).jpg")`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      borderRadius: theme.spacing(0.5),
                    }}
                  />
                </Grid>

                {/* Center Text Content */}
                <Grid container item size={{ xs: 12, sm: 9 }}>
                  <Stack sx={{ paddingRight: theme.spacing(2), paddingBottom: theme.spacing(1) }}>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                      <Typography
                        variant="h5"
                        sx={{
                          marginTop: theme.spacing(0.5),
                          marginLeft: theme.spacing(1),
                        }}
                      >
                        {"Madison Parker"}
                      </Typography>
                    </Box>
                    <Divider
                      sx={{
                        width: "calc(100% - 8px)",
                        marginLeft: theme.spacing(1),
                        marginTop: "0px",
                        marginBottom: "8px",
                        background: `linear-gradient(to right, ${theme.palette.text.primary}, transparent)`,
                        border: "none",
                        height: "2px",
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        paddingLeft: theme.spacing(1),
                        lineHeight: 1.5,
                      }}
                    >
                      Madison is graduating college earlier this year and just celebrated her 22nd
                      birthday yesterday. She's excited to finally start building her career, but
                      the transition into adulthood has been financially overwhelming. Between
                      student loans, apartment expenses, and credit card balances that grew during
                      school, she feels like she's constantly trying to catch up. Lower interest
                      payments and breathing room would help her feel more in control of her future.
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              {/* Right Action Block */}
              <Grid
                item
                size={{ xs: 12, sm: 3 }}
                sx={{
                  display: "flex",
                  padding: theme.spacing(1),
                }}
              >
                <Button
                  sx={{
                    height: "auto",
                    borderRadius: theme.spacing(1),
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  tooltipTitle="Start Conversation"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    startWebchat(2);
                  }}
                >
                  <Stack alignItems="center">
                    <Typography
                      variant="h5"
                      sx={{
                        borderBottom: `2px solid`,
                        margin: theme.spacing(1),
                        paddingBottom: theme.spacing(1),
                        marginBottom: 0,
                      }}
                    >
                      {"Madison Parker"}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        margin: theme.spacing(1),
                      }}
                    >
                      Click Here
                    </Typography>
                  </Stack>
                </Button>
              </Grid>
            </Grid>

            {/* User 3 */}
            <Grid
              item
              container
              padding={0}
              size={{ xs: 12 }}
              sx={{
                borderRadius: theme.spacing(1),
                padding: "4px",
                display: "flex",
                flexDirection: "row",
                backgroundColor: theme.palette.primary.contrastText,
                border: `1px solid ${alpha(theme.palette.common.black, 0.15)}`,
                boxShadow: theme.shadows[3],
                marginBottom: theme.spacing(2),
                overflow: "hidden",
              }}
            >
              <Grid container item size={{ xs: 12, sm: 9 }} flexDirection={"row"}>
                {/* Left Image Section */}
                <Grid container item size={{ xs: 12, sm: 3 }} sx={{ padding: "4px" }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: "140px",
                      backgroundImage: `url("https://omni-public-media.s3.us-west-2.amazonaws.com/test/demo/userImages/userImage(3).jpg")`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      borderRadius: theme.spacing(0.5),
                    }}
                  />
                </Grid>

                {/* Center Text Content */}
                <Grid container item size={{ xs: 12, sm: 9 }}>
                  <Stack sx={{ paddingRight: theme.spacing(2), paddingBottom: theme.spacing(1) }}>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                      <Typography
                        variant="h5"
                        sx={{
                          marginTop: theme.spacing(0.5),
                          marginLeft: theme.spacing(1),
                        }}
                      >
                        {"Frank Miller"}
                      </Typography>
                    </Box>
                    <Divider
                      sx={{
                        width: "calc(100% - 8px)",
                        marginLeft: theme.spacing(1),
                        marginTop: "0px",
                        marginBottom: "8px",
                        background: `linear-gradient(to right, ${theme.palette.text.primary}, transparent)`,
                        border: "none",
                        height: "2px",
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        paddingLeft: theme.spacing(1),
                        lineHeight: 1.5,
                      }}
                    >
                      Frank has spent decades working hard and preparing for retirement while
                      supporting his family. He and his wife will celebrate their 40th anniversary
                      next month, and they have always loved traveling to Hawaii whenever they could
                      afford it. Although his finances are stable, he wants to reduce unnecessary
                      monthly costs before retirement. Refinancing his vehicle at a lower rate —
                      while taking out a little extra cash for an anniversary trip — feels like a
                      smart and rewarding move.
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              {/* Right Action Block */}
              <Grid
                item
                size={{ xs: 12, sm: 3 }}
                sx={{
                  display: "flex",
                  padding: theme.spacing(1),
                }}
              >
                <Button
                  sx={{
                    height: "auto",
                    borderRadius: theme.spacing(1),
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  tooltipTitle="Start Conversation"
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    startWebchat(3);
                  }}
                >
                  <Stack alignItems="center">
                    <Typography
                      variant="h5"
                      sx={{
                        borderBottom: `2px solid`,
                        margin: theme.spacing(1),
                        paddingBottom: theme.spacing(1),
                        marginBottom: 0,
                      }}
                    >
                      {"Frank Miller"}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        margin: theme.spacing(1),
                      }}
                    >
                      Click Here
                    </Typography>
                  </Stack>
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid item spacing={2} size={{ xs: 12, sm: 4 }} width={"100%"}>
            {isWebchatOpen === true && (
              <Container
                connected={clientState !== "disconnected"}
                style={{
                  height: "calc(100vh - 82px)",
                  width: "100%",
                  display: isWebchatOpen ? "flex" : "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    border: "none",
                    boxShadow: theme.shadows[2],
                    zIndex: 1,
                    columnGap: "8px",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    backgroundColor: `${config.customColors.primaryColor}dd`,
                  }}
                >
                  <OmniAvatar
                    sx={{
                      width: "64px",
                      height: "64px",
                      boxShadow: theme.shadows[2],
                      background: "radial-gradient(#fdfef400, #aaaaaa)",
                    }}
                    src={config.botAvatar}
                  />
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      color: config.customColors.primaryContrastText,
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={"500"}
                      sx={{
                        textShadow: `1px 1px 1px ${config.customColors.secondaryContrastText}88`,
                      }}
                    >
                      {/* {config.botName.split(" ")[0]} */}
                      {config.title}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        marginTop: "-6px",
                      }}
                    >
                      {config.botName}
                    </Typography>
                  </Box>
                  {config && config.socialMedia ? (
                    <Box display={"flex"} columnGap={"0px"}>
                      {config.socialMedia.map(social => (
                        <AppBarIconButton>
                          <SocialIcon
                            target="_blank"
                            url={social}
                            bgColor="transparent"
                            style={{
                              height: 36,
                              width: 36,
                            }}
                            fgColor={config.customColors.primaryContrastText}
                          />
                        </AppBarIconButton>
                      ))}
                    </Box>
                  ) : null}
                  <Box
                    style={{
                      color: config.customColors.primaryContrastText,
                    }}
                  >
                    <AppBarIconButton
                      size="large"
                      edge="start"
                      color="inherit"
                      id="omni-primary-menu-button"
                      onClick={e => handleClick(e)}
                    >
                      {!open ? <MenuIcon /> : <CloseRoundedIcon />}
                    </AppBarIconButton>

                    <OmniMenu
                      id="omni-primary-menu"
                      MenuListProps={{
                        "aria-labelledby": "omni-primary-menu-button",
                      }}
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                    >
                      <MenuItem
                        component="label"
                        onClick={() => {
                          setTTSValue(!ttsValue);
                          handleClose();
                        }}
                      >
                        <ListItemIcon>
                          {!ttsValue && <RecordVoiceOverIcon fontSize="small" />}
                          {ttsValue && <VoiceOverOffIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText>
                          {ttsValue ? "Turn off Audio" : "Listen To Audio"}
                        </ListItemText>
                      </MenuItem>

                      <Divider
                        sx={{
                          display: "flex",
                        }}
                      />

                      <MenuItem
                        onClick={() => {
                          handleClose();
                          newConversation();
                        }}
                      >
                        <ListItemIcon>
                          <AddComment fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>New Conversation</ListItemText>
                      </MenuItem>
                    </OmniMenu>
                  </Box>
                </Box>

                <MessageList
                  botAvatar={config.botAvatar}
                  botName={config.botName}
                  botDescription={config.botDescription}
                  isTyping={isTyping}
                  headerMessage="Chat History"
                  showMarquee={true}
                  messages={enrichedMessages}
                  sendMessage={client?.sendMessage}
                  showIncomingMessageAvatar={true}
                />
                <Composer
                  disableComposer={false}
                  isReadOnly={false}
                  allowFileUpload={true}
                  connected={clientState !== "disconnected"}
                  sendMessage={client?.sendMessage}
                  uploadFile={client?.uploadFile}
                  composerPlaceholder="Type a message..."
                />
                {ttsValue === true && (
                  <Box
                    style={{
                      position: "absolute",
                      right: 58,
                      bottom: 21,
                      zIndex: 1001,
                      height: 32,
                      width: 32,
                      color: "#5f6276",
                    }}
                  >
                    <OmniTextToSpeechPlayer
                      textToSpeechQueue={textToSpeechQueue}
                      setTextToSpeechQueue={setTextToSpeechQueue}
                      volume={volume}
                      setVolume={setVolume}
                    />
                  </Box>
                )}
              </Container>
            )}
          </Grid>
        </Grid>
      </PortalGridContent>
    </Portal>
  );
};

export default withRouter(DemoCUPortal);
